import {
  Button,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Spin,
  Table,
} from "antd";
import React, { useEffect, useState } from "react";
import ModalConfirmation from "./modal-confirmation";
import PalletHistory from "./pallet-history";
import API from "../../../services/api-request";
import dayjs from "dayjs";
import { getDataLogin } from "../../../utils/getDataLogin";

const ModalFooter = ({ setAction, setOpenConfirmModal }) => {
  const handleOpenModal = (action) => {
    setAction(action);
    setOpenConfirmModal(true);
  };

  const isManagement =
    getDataLogin()?.privilege?.name?.toLowerCase() === "management";

  if (!isManagement) {
    return;
  }

  return (
    <Row gutter={20}>
      <Col span={12}>
        <Button
          style={{ width: "100%" }}
          type="primary"
          onClick={() => handleOpenModal("move")}
        >
          Move
        </Button>
      </Col>

      <Col span={12}>
        <Button
          style={{ width: "100%", backgroundColor: "#ffa035" }}
          type="primary"
          onClick={() => handleOpenModal("merge")}
        >
          Merge
        </Button>
      </Col>
    </Row>
  );
};

const ModalDetail = ({ open, onOk, onCancel, data, getAreaData }) => {
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [historyModal, showHistoryModal] = useState(false);
  const [action, setAction] = useState(null);
  const [detailForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [fishData, setFishData] = useState([]);
  const [palletData, setPalletData] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 3,
    total: 0,
    totalPages: 0,
  });
  const [editingIndex, setEditingIndex] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [stockUpdateForm, setStockUpdateForm] = useState({
    weight: null,
    specification: null,
  });

  const getDataDetail = (id, page = 1) => {
    if (page === 1) {
      setLoading(true);
    } else {
      setTableLoading(true);
    }

    API.Get(`area/${id}/stock?limit=${pagination.pageSize}&page=${page}`)
      .then((res) => {
        const formatSelect = res?.data?.stock?.stock?.map((item, index) => ({
          ...item,
          no: (page - 1) * pagination.pageSize + index + 1,
        }));

        setPagination({
          current: res?.data?.stock?.currentPage,
          pageSize: 3,
          total: res?.data?.stock?.total,
          totalPages: res?.data?.stock?.totalPages,
        });

        detailForm.setFieldsValue({
          ...res?.data,
          pallet_id: res?.data?.pallet_id,
          received_at: res?.data?.received_at
            ? dayjs(res?.data?.received_at)
            : "",
          amount: res?.data?.amount?.toLocaleString("id-ID"),
          duration: formatSelect[0]?.duration,
          stock: formatSelect,
        });

        setFishData(formatSelect);
        setPalletData({
          pallet_id: res?.data?.pallet_id,
          pallet_name: res?.data?.pallet_name,
        });
      })
      .catch((err) => {
        console.error(err);
        message.info(`${err?.response?.data?.error}`);
      })
      .finally(() => {
        setLoading(false);
        setTableLoading(false);
      });
  };

  const handleEditStock = (value) => {
    setEditLoading(true);

    const payload = {
      weight: stockUpdateForm?.weight,
      specification: stockUpdateForm?.specification,
    };

    API.Post(`area/stock/${value?.stockid}`, payload)
      .then((res) => {
        message.success("Stock data updated!");
        setEditingIndex(null);
      })
      .catch((err) => {
        console.log(err);
        message.error("Failed to update stock data!");
      })
      .finally(() => {
        setEditLoading(false);
        getDataDetail(data.id, 1);
        getAreaData();
      });
  };

  useEffect(() => {
    if (open && data?.id) {
      getDataDetail(data.id, 1);
    }
  }, [open]);

  const fishColumns = [
    {
      title: "No",
      dataIndex: "no",
      key: "no",
      width: 60,
      render: (no) => no,
    },
    {
      title: "Produk",
      dataIndex: "product_name",
      key: "product_name",
      sorter: (a, b) => {
        if (a.product_name === null || a.product_name === "-") return 1;
        if (b.product_name === null || b.product_name === "-") return -1;
        return a.product_name.localeCompare(b.product_name);
      },
      render: (name) => {
        if (name === null || name === "-") {
          return "-";
        } else {
          return name;
        }
      },
    },
    {
      title: "Spesifikasi",
      dataIndex: "specification",
      key: "specification",
      sorter: (a, b) => {
        if (a.specification === null || a.specification === "-") return 1;
        if (b.specification === null || b.specification === "-") return -1;
        return a.specification.localeCompare(b.specification);
      },
      render: (specification, data) => {
        const isEditing = editingIndex === data?.no;
        if (isEditing) {
          return (
            <Input
              style={{
                width: "100%",
              }}
              defaultValue={specification}
              onChange={(e) =>
                setStockUpdateForm({
                  ...stockUpdateForm,
                  specification: e.target.value ?? specification,
                })
              }
              placeholder="Spesifikasi"
            />
          );
        } else {
          if (specification === null || specification === "-") {
            return "-";
          } else return specification;
        }
      },
    },
    {
      title: "Berat",
      dataIndex: "weight",
      key: "weight",
      width: 150,
      sorter: (a, b) => {
        if (a.weight === null || a.weight === "-") return 1;
        if (b.weight === null || b.weight === "-") return -1;
        return a.weight - b.weight;
      },
      render: (weight, data) => {
        if (weight === null || weight === "-") {
          return "-";
        } else {
          const isEditing = editingIndex === data?.no;

          if (isEditing) {
            return (
              <InputNumber
                style={{
                  width: "100%",
                }}
                defaultValue={Math.round(weight)}
                controls={false}
                min={0}
                placeholder="Berat"
                suffix="Kg"
                onChange={(e) =>
                  setStockUpdateForm({
                    ...stockUpdateForm,
                    weight: e ?? weight,
                  })
                }
              />
            );
          } else return Math.round(weight) + " Kg";
        }
      },
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 220,
      render: (_, data) => {
        const isEditing = editingIndex === data?.no;

        return (
          <Row gutter={[8, 8]}>
            <Col>
              {isEditing ? (
                <Row gutter={8}>
                  <Col span={12}>
                    <Button
                      type="primary"
                      onClick={() => {
                        handleEditStock(data);
                      }}
                      loading={editLoading}
                    >
                      Save
                    </Button>
                  </Col>

                  <Col span={12}>
                    <Button
                      type="primary"
                      onClick={() => {
                        setEditingIndex(null);
                      }}
                      loading={editLoading}
                      style={{ backgroundColor: "#faad14" }}
                    >
                      Cancel
                    </Button>
                  </Col>
                </Row>
              ) : (
                <Button
                  type="primary"
                  onClick={() => {
                    setEditingIndex(data?.no);
                    setStockUpdateForm({ ...data });
                  }}
                >
                  Edit
                </Button>
              )}
            </Col>
          </Row>
        );
      },
    },
  ];

  return (
    <>
      <Modal
        open={open}
        onOk={onOk}
        onCancel={() => {
          setEditingIndex(null);
          onCancel();
        }}
        title={
          <Flex justify="space-between">
            <div>Detail Data {data?.name}</div>
            <Button
              type="primary"
              style={{ marginTop: "-0.3rem", marginRight: "2rem" }}
              onClick={() => showHistoryModal(true)}
            >
              History Pallet
            </Button>
          </Flex>
        }
        width={"40%"}
        footer={
          <ModalFooter
            onOk={onOk}
            onCancel={onCancel}
            setAction={setAction}
            setOpenConfirmModal={setOpenConfirmModal}
          />
        }
      >
        <hr style={{ margin: "1rem 0 1rem 0" }} />
        {loading ? (
          <Flex justify="center" align="center" style={{ height: "15svh" }}>
            <Spin />
          </Flex>
        ) : (
          <>
            <Form layout="vertical" form={detailForm}>
              <Row gutter={20}>
                <Col span={8}>
                  <Form.Item label="No. Palet" name="pallet_name">
                    <Input readOnly placeholder="No Palet" />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item label="Client" name="client_name">
                    <Input readOnly placeholder="Client" />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item label="Tanggal Inbound" name="received_at">
                    <DatePicker
                      allowClear={false}
                      style={{ width: "100%" }}
                      inputReadOnly
                      open={false}
                      placeholder="Tanggal Inbound"
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item label="Lama Penyimpanan" name="duration">
                    <Input
                      suffix={"Hari"}
                      readOnly
                      placeholder="Lama Penyimpanan"
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item label="Total Biaya" name="amount">
                    <Input prefix={"Rp"} readOnly placeholder="Biaya" />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item label="Nomor Lantai" name="row_number">
                    <Input readOnly placeholder="Nomor Lantai" />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item label="Section" name="section">
                    <Input readOnly placeholder="Section" />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item label="Lokasi" name="column_name">
                    <Input readOnly placeholder="Lokasi" />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item label="Tingkat" name="level">
                    <Input readOnly placeholder="Tingkat" />
                  </Form.Item>
                </Col>
              </Row>
            </Form>

            <div>
              <Table
                columns={fishColumns}
                dataSource={fishData}
                pagination={{
                  ...pagination,
                  position: ["topRight"],
                  onChange: (page) => {
                    getDataDetail(data.id, page);
                  },
                }}
                style={{ marginTop: "-1rem" }}
                rowKey={(dataSource) => dataSource.no}
                loading={tableLoading}
              />
            </div>
          </>
        )}
      </Modal>

      <ModalConfirmation
        action={action}
        open={openConfirmModal}
        onCancel={() => setOpenConfirmModal(false)}
        closeParentModal={onCancel}
        floor={{ id: data?.id, name: data?.name }}
        pallet={{ id: palletData?.pallet_id, name: palletData?.pallet_name }}
        getAreaData={getAreaData}
      />

      <Modal
        width={"80%"}
        open={historyModal}
        onCancel={() => showHistoryModal(false)}
        footer={null}
      >
        <PalletHistory data={data} />
      </Modal>
    </>
  );
};

export default ModalDetail;
